/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-shadow */
import {
  castTabDirection,
  ModalRef,
  Queue,
  QueueStrategy,
  useMicroservices,
  useRuleEngineCache,
  useTranslation,
} from '@msdk/common'
import {
  DataRuleDefinition,
  disableTree,
  Entity,
  EntityAcl,
  EntityContext,
  EntityModel,
  FieldType,
  findNode,
  FormCustomTabDefinition,
  FormEntityViewDefinition,
  FormFieldDefinition,
  FormFormDefinition,
  FormMode,
  FormModel,
  FormNode,
  FormTabDefinition,
  formTraverse,
  FormViewDefinition,
  GenericFieldValue,
  getControl,
  getEntityModelForFormField,
  isTreeValid,
  ListValue,
  ReloadViewsOptions,
  ValidationMessageParameters,
} from '@msdk/definitions'
import { initializeMetricsAPI } from '@msdk/telemetry'
import {
  Box,
  createComponentWrapperWithRef,
  css,
  styled,
  TabEntity,
  Tabs,
  TabsContainer,
  THEME_CONST,
  uiTokens,
  useComponentLibrary,
} from '@msdk/ui'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import { useRecoilState_TRANSITION_SUPPORT_UNSTABLE } from 'recoil'

import { ClearJournal, runDataRules, runListDataRules } from '../../hooks/run-data-rules'
import { runOnChange } from '../../hooks/run-onchange'
import { runListValidations, runValidations } from '../../hooks/run-validations'
import { ChangeArgs, ExternalValidation } from '../../models/form'
import { $entityCollection } from '../../state'
import { arrayToRecord, getVisibleTabs, invisibleFieldsToDataRules, nonWritableFieldsToDataRules } from '../../utils'
import { ValidatedForm } from '../../utils/save-validation'
import { hasErrorMessage } from '../../utils/validation-visibility'
import { EmbeddedForm } from '../EmbeddedForm'
import { EmbeddedFormFederationWrapper } from '../EmbeddedForm/EmbeddedFormFederationWrapper'
import { EmbeddedView } from '../EmbeddedView'
import { EmbeddedEntityViewFederationWrapper } from '../EmbeddedView/EmbeddedEntityViewFederationWrapper'
import { EmbeddedViewFederationWrapper } from '../EmbeddedView/EmbeddedViewFederationWrapper'
import { ConditionalFieldRenderer, ConditionalFieldRendererBehaviourProps } from '../Fields'
import { I18nWrapper } from '../I18nWrapper'
import { ConditionalReadonlyFieldRenderer } from '../ReadonlyFields'
import {
  FormLength,
  getFormLength,
  getValidationIssues,
  getValidationTarget,
  ValidationIssue,
  ValidationPanel,
} from '../ValidationPanel'
import {
  applyExternalValidations,
  type ExternalValidations,
  isSameExternalValidation,
} from '../ValidationPanel/external-validation'
import { CustomLogicInvoker } from './custom-logic-invoker'
import { CustomTab } from './CustomTab'
import { EntityView } from './EntityView'
import { Group } from './Group'
import { TabLabel } from './TabLabel'
import { ValidationTabLabel } from './ValidationTabLabel'

/**
 * Right padding fix
 */
const MuiPaddingFix = css`
  &:not(:has(.MuiTabs-root):not(:has(& .MuiTabs-root))) {
    padding: ${THEME_CONST.defaultPadding}px;
    padding-right: 0;
  }
`

const MuiFormContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$mode' && prop !== '$centered' && prop !== '$isEmbedded',
})<{
  $mode: string
  $centered: boolean | undefined
  $isEmbedded: boolean | undefined
}>`
  width: 100%;
  height: 100%;

  ${(props) => !props.$isEmbedded && MuiPaddingFix}
`

/**
 * Right padding fix
 */
const NovaPaddingFix = css`
  &:not(:has(.MuiTabs-root):not(:has(& .MuiTabs-root))) {
    padding: ${uiTokens.spacing.padding.large}px;
    padding-right: 0;
  }
  .ModalFormDynamic &:not(:has(.MuiTabs-root):not(:has(& .MuiTabs-root))) {
    padding-right: ${uiTokens.spacing.padding.large}px;
  }
`

/**
 * Removes padding of the first groups in embedded forms
 */
const NovaCardFix = css`
  > .FormGroup > .FormGroup__content {
    padding: 0;
  }
  > .FormGroup > .FormGroup__content > .FormGroup__item > .FormGroup > .FormGroup__content {
    padding: 0;
  }
`

const NovaFormContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$mode' && prop !== '$centered' && prop !== '$isEmbedded',
})<{
  $mode: string
  $centered: boolean | undefined
  $isEmbedded: boolean | undefined
}>`
  width: 100%;
  height: 100%;

  ${(props) => !props.$isEmbedded && NovaPaddingFix}
  ${(props) => props.$isEmbedded && NovaCardFix}

  &:has(.MuiTabs-root.MuiTabs-vertical) {
    padding-left: 0;
  }
`

const FormContainer = createComponentWrapperWithRef(
  {
    mui: MuiFormContainer,
    nova: NovaFormContainer,
  },
  'FormContainer',
)

const trackMetric = initializeMetricsAPI().pushMetric
const FIELD_FOCUS_SELECTOR = ['input:not([disabled])', 'textarea:not([disabled])', 'select:not([disabled])'].join(',')
const FIELD_FALLBACK_FOCUS_SELECTOR = ['button:not([disabled])', '[tabindex]:not([tabindex="-1"])'].join(',')
const PENDING_TARGET_TIMEOUT_MS = 5000

function getRootTabId(root: FormNode): string | undefined {
  let node: FormNode | undefined = root
  while (node?.value.type === 'Group') {
    node = node.firstChild
  }

  return node?.value.type === 'Tab' || node?.value.type === 'CustomTab' ? node.id : undefined
}

interface Props {
  root: FormNode
  entity: Entity | null
  additionalEntities: Entity[]
  entityModel: EntityModel
  formModel: FormModel
  entityLoaded: boolean
  acl: EntityAcl
  invoker: CustomLogicInvoker
  mode: FormMode
  modalRef?: ModalRef
  onRenderCompleted?: () => void
  onDataChanged?: () => void
  className?: string
  validationRef?: React.MutableRefObject<(() => Promise<ValidatedForm | null>) | null>
  onChangeTab?: (newValue: number) => void
  selectedTab?: number
  startingTab?: number
  moveToTab?: (newValue: number) => void
  isEmbedded?: boolean
}

export function DynamicForm({
  root,
  entity,
  additionalEntities,
  entityModel,
  formModel,
  entityLoaded,
  acl,
  invoker,
  mode,
  modalRef,
  onRenderCompleted,
  onDataChanged,
  className,
  validationRef,
  selectedTab,
  startingTab,
  onChangeTab,
  moveToTab,
  isEmbedded,
}: Props) {
  const t = useTranslation()
  const microservices = useMicroservices()
  const lib = useComponentLibrary()
  const [currentEntity, setCurrentEntity] = useState(entity as Entity)
  const [currentAdditionalEntities, setCurrentAdditionalEntities] = useState(additionalEntities)
  const currentEntityRef = useRef(entity as Entity)
  const additionalEntitiesRef = useRef(additionalEntities)
  const [entityCollection, setEntityCollection] = useRecoilState_TRANSITION_SUPPORT_UNSTABLE($entityCollection)
  const [rootNode, setRootNode] = useState(root)
  const rootTabId = getRootTabId(rootNode)
  const [selectedTabs, setSelectedTabs] = useState<Record<string, number>>({})
  const [pendingTarget, setPendingTarget] = useState<{
    fallbackNodeId?: string
    id: string
    sequence: number
  }>()
  const formRef = useRef<HTMLDivElement>(null)
  // Validations reported by custom fields, kept outside the tree because every rule run rebuilds its messages.
  const externalValidations = useRef<ExternalValidations>(new Map())

  const { cachedValidation, cachedDataRules, cachedOnChange } = useRuleEngineCache(entityModel.name, formModel.name)

  const [queue] = useState<Queue>(new Queue(QueueStrategy.Latest))
  const [aclRules, setAclRules] = useState<DataRuleDefinition[]>([])

  const [embeddedViewsRefreshVersion, setEmbeddedViewsRefreshVersion] = useState(0)
  const handleEmbeddedViewDataChanged = () => {
    setEmbeddedViewsRefreshVersion((version) => version + 1)
    onDataChanged?.()
  }

  const [targetedViewVersions, setTargetedViewVersions] = useState<Record<string, number>>({})
  const refreshView = useCallback((viewName: string) => {
    setTargetedViewVersions((prev) => ({ ...prev, [viewName]: (prev[viewName] ?? 0) + 1 }))
  }, [])

  useEffect(() => {
    if (entity?.fields) {
      const currentEntry = entityCollection[entity.internalId as string]
      setCurrentEntity({ ...entity })
      setCurrentAdditionalEntities([...additionalEntities])
      currentEntityRef.current = entity
      additionalEntitiesRef.current = additionalEntities
      setEntityCollection((currentEntityCollection) => {
        const current = currentEntityCollection[entity.internalId as string]
        return {
          ...currentEntityCollection,
          [entity.internalId as string]: {
            entity,
            additionalEntities: currentAdditionalEntities,
            rootNode,
            fieldValidations: [],
            pendingChanges: !!currentEntry?.pendingChanges,
            acl: current?.acl as EntityAcl,
          },
        }
      })
    }
    return () => {
      setEntityCollection((currentCollection) => {
        const newCollection = { ...currentCollection }
        if (entity?.fields) {
          delete newCollection[entity.internalId as string]
        }
        return newCollection
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity])

  const runDataRulesAndValidations = async (
    entityContext: EntityContext,
    clonedEntityContext: EntityContext,
    acl?: EntityAcl,
    pendingChanges?: boolean,
    aclRulesSet?: DataRuleDefinition[],
  ) => {
    //dataRules
    const rCache = { ...cachedDataRules }
    const clearJournal = new ClearJournal(entityContext)
    rCache.rules = [...rCache.rules, ...(aclRulesSet ?? aclRules)]
    runDataRules(root, entityContext, rCache, false, clearJournal)
    runListDataRules(root, clonedEntityContext, cachedDataRules, false, clearJournal)

    //validation
    runValidations(root, entityContext, cachedValidation)
    runListValidations(root, entityContext, cachedValidation)
    await invoker.validate(entityContext)
    const customVal = entityContext.customValidaitons.filter((v) => !v.onList)
    const onListCustomVal = entityContext.customValidaitons.filter((v) => v.onList)
    const clValidations = {
      ...cachedValidation,
      cachedRules: arrayToRecord(customVal, 'field'),
      cachedListRules: arrayToRecord(onListCustomVal, 'onList'),
    }
    runValidations(root, entityContext, clValidations)
    runListValidations(root, entityContext, clValidations)

    //dataRules
    runDataRules(root, clonedEntityContext, rCache, true, clearJournal)
    runListDataRules(root, clonedEntityContext, cachedDataRules, true, clearJournal)

    applyExternalValidations(root, externalValidations.current)

    //update state
    const unboxed = entityContext.unbox()
    unstable_batchedUpdates(() => {
      setCurrentEntity(unboxed.entity)
      setCurrentAdditionalEntities(unboxed.additionalEntities)
      currentEntityRef.current = unboxed.entity
      additionalEntitiesRef.current = unboxed.additionalEntities
      setRootNode({ ...root })
      setEntityCollection((current) => ({
        ...current,
        [unboxed.entity.internalId as string]: {
          entity: unboxed.entity,
          additionalEntities: unboxed.additionalEntities,
          rootNode: root,
          pendingChanges:
            pendingChanges !== undefined
              ? pendingChanges
              : !!current[unboxed.entity.internalId as string]?.pendingChanges,
          acl: acl ?? current[unboxed.entity.internalId as string]?.acl,
        },
      }))
    })
  }

  useEffect(() => {
    if (entityLoaded && entity) {
      formTraverse(root, (node) => {
        if (node.value.validationRevealed === undefined) {
          node.value.validationRevealed = hasErrorMessage(node)
        }
      })

      const fetch = async () => {
        const newEntity = { ...entity, fields: { ...entity.fields } }
        const additionals = currentAdditionalEntities.map((e) => {
          return { ...e, fields: { ...e.fields } }
        })
        const clonedAdditionals = currentAdditionalEntities.map((e) => {
          return { ...e, fields: { ...e.fields } }
        })
        const clonedEntity = { ...entity, fields: { ...entity.fields } }

        const invisibleRules = invisibleFieldsToDataRules(acl.invisibleFields)
        const readonlyRules = nonWritableFieldsToDataRules(acl.nonWritableFields)
        const aclRules = [...invisibleRules, ...readonlyRules]
        setAclRules(aclRules)

        const entityContext = new EntityContext(newEntity, root, additionals, formModel)
        const clonedEntityContext = new EntityContext(clonedEntity, root, clonedAdditionals, formModel)

        const startTime = new Date().getUTCMilliseconds()
        console.groupCollapsed('After Entity Load')
        console.time('Running Rule engine')
        console.log('Context information: ')
        console.log('%o the current form object', root)
        console.log('%o the updated entity', entityContext)
        await invoker.postLoad(entityContext)
        formTraverse(root, (node) => {
          if (hasErrorMessage(node)) {
            node.value.validationRevealed = true
          }
        })

        // here keep validation is true to allow validations
        // coming from server to be shown, whe still have to
        // reset the tree state to allow proper execution of
        // data rules that alter visibility or readonly state
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        resetFormTree(entityContext, true)

        // pass undefined so runDataRulesAndValidations reads pendingChanges from current state
        // via functional setter, avoiding a stale closure after postLoad may call rebindEntity
        await runDataRulesAndValidations(entityContext, clonedEntityContext, acl, undefined, aclRules)

        const endTime = new Date().getUTCMilliseconds()
        console.timeEnd('Running Rule engine')
        console.groupEnd()

        trackMetric({
          value: endTime - startTime,
          attributes: { formName: formModel.name, entityName: entityModel.name },
        })

        if (!acl.canUpdate || mode === 'Readonly') {
          disableTree(root)
        }
      }
      fetch()
        .then(() => onRenderCompleted?.())
        .catch(console.error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityLoaded, root, mode])

  useEffect(() => {
    if (!pendingTarget) {
      return
    }

    const form = formRef.current
    let observer: MutationObserver | undefined
    let timeout: number | undefined

    const focusTarget = (): boolean => {
      const targets = Array.from(form?.querySelectorAll<HTMLElement>('[data-validation-target]') ?? [])
      const target =
        targets.find((element) => element.dataset.validationTarget === pendingTarget.id) ??
        targets.find((element) => element.dataset.validationNode === pendingTarget.fallbackNodeId)

      if (!target) {
        return false
      }

      target.scrollIntoView({ block: 'center' })
      const focusable =
        target.querySelector<HTMLElement>(FIELD_FOCUS_SELECTOR) ??
        target.querySelector<HTMLElement>(FIELD_FALLBACK_FOCUS_SELECTOR) ??
        target
      focusable.focus({ preventScroll: true })
      return true
    }

    const finish = () => {
      observer?.disconnect()
      window.clearTimeout(timeout)
      setPendingTarget(undefined)
    }

    // Wait for a newly selected tab to mount before scrolling and focusing its field.
    const frame = window.requestAnimationFrame(() => {
      if (focusTarget() || !form || typeof MutationObserver === 'undefined') {
        setPendingTarget(undefined)
        return
      }

      // Custom fields can render their inputs asynchronously (e.g. remote micro-frontends).
      observer = new MutationObserver(() => {
        if (focusTarget()) {
          finish()
        }
      })
      observer.observe(form, { childList: true, subtree: true })
      timeout = window.setTimeout(finish, PENDING_TARGET_TIMEOUT_MS)
    })

    return () => {
      window.cancelAnimationFrame(frame)
      observer?.disconnect()
      window.clearTimeout(timeout)
    }
  }, [pendingTarget, selectedTabs])

  const tabsDirection = castTabDirection(formModel.tabDirection)

  const onFieldChange = async (args: ChangeArgs) => {
    const newEntity = { ...currentEntityRef.current, fields: { ...currentEntityRef.current.fields } }
    const additionals = additionalEntitiesRef.current.map((e) => {
      return { ...e, fields: { ...e.fields } }
    })

    const entityContext = new EntityContext(newEntity, root, additionals, formModel)
    entityContext.fields[args.fieldName] = args.value
    const unboxed = entityContext.unbox()

    setCurrentEntity(unboxed.entity)
    setCurrentAdditionalEntities(unboxed.additionalEntities)
    currentEntityRef.current = unboxed.entity
    additionalEntitiesRef.current = unboxed.additionalEntities

    /*
     * using a queue for field change events prevents multiple events to execute concurrently
     * in case of a form with heavy async custom logic this ensures that multiple events don't conflict
     * with each other and that only the last triggered event is applying the correct effects form custom logic
     */
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    queue.enqueue(() => onChangeTask(args, unboxed.entity, unboxed.additionalEntities))
  }

  const onChangeTask = async (args: ChangeArgs, newEntity: Entity, additionalEntities: Entity[]) => {
    const startTime = new Date().getMilliseconds()
    console.groupCollapsed('OnChange')
    console.time('Running Rule engine')
    console.log('Context information: ')
    console.log('%o the current form object', root)
    console.log('%o the updated entity', newEntity)

    //onChange
    const entityContext = new EntityContext(newEntity, root, additionalEntities, formModel)

    runOnChange(entityContext, cachedOnChange, args.fieldName, entityModel, formModel)
    await invoker.onFieldChanged(entityContext, args)
    const clonedEntity = { ...newEntity, fields: { ...newEntity.fields } }
    const clonedAdditionals = additionalEntities.map((e) => {
      return { ...e, fields: { ...e.fields } }
    })
    const clonedEntityContext = new EntityContext(clonedEntity, root, clonedAdditionals, formModel)

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    resetFormTree(entityContext)

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    await runDataRulesAndValidations(entityContext, clonedEntityContext, undefined, true)

    console.timeEnd('Running Rule engine')
    console.groupEnd()
    const endTime = new Date().getMilliseconds()
    trackMetric({
      value: endTime - startTime,
      attributes: { formName: formModel.name, entityName: entityModel.name },
    })
  }

  /**
   * retrieves the node value considering that a cell of a row inside a listfield
   * cannot access the entity directly but has to first retrieve the list value
   */
  const getNodeValue = (entity: EntityContext, node: FormNode): GenericFieldValue => {
    if (node.value.listName) {
      const listValue = entity.fields[node.value.listName] as ListValue
      const rowId = node.value.rowId as string
      const row = listValue.value?.find((r) => r.id === rowId) as Entity
      return row.fields[node.id]
    }
    return entity.fields[node.id] !== undefined ? entity.fields[node.id] : node.value.data
  }

  const resetFormTree = (entityContext: EntityContext, keepValidations?: boolean) => {
    const revealed = new Set<string>()
    const priorMessages = new Map<
      string,
      { messages: string[]; params: string[]; messageParams?: ValidationMessageParameters[] }
    >()
    formTraverse(root, (node) => {
      const target = getValidationTarget(node)
      if (node.value.validationRevealed) {
        revealed.add(target)
      }
      if (keepValidations) {
        priorMessages.set(target, {
          messages: node.value.validationMessages,
          params: node.value.validationParams,
          messageParams: node.value.validationMessageParams,
        })
      }
    })

    formTraverse(root, (node) => {
      if (node.value.fieldDefinition?.fieldDefinitionType === 'List') {
        const newControl = getControl(
          {
            entity: entityContext,
            fieldDef: node.value.fieldDefinition,
            formDef: node.value.formDefinition as FormFieldDefinition,
            prevChildren: undefined,
          },
          entityModel,
          formModel,
          formModel.name,
        )
        node.value = newControl.value
        node.firstChild = newControl.firstChild
      }

      const previous = priorMessages.get(getValidationTarget(node))
      node.value.validationMessages = previous?.messages ?? []
      node.value.validationRevealed = revealed.has(getValidationTarget(node))
      node.value.validationParams = previous?.params ?? []
      node.value.validationMessageParams = previous?.messageParams ?? []
      node.value.state = 'Enabled'
      node.value.visibility = 'Visible'
      node.value.validationState = 'Pending'
      node.value.isDirty = false
      node.value.selectOptions = { keep: [], remove: [] }
      node.value.validationFlags = 0
      node.value.data = getNodeValue(entityContext, node)
      if (node.value.fieldDefinition) {
        const fieldName = node.value.fieldDefinition.name
        const formFieldName = (node.value.formDefinition as FormFieldDefinition)?.name
        const model = getEntityModelForFormField(formFieldName, entityModel, formModel)
        const rval = model.getNumericValidation(fieldName) || formModel.getNumericValidation(formFieldName)
        node.value.range = undefined
        if (rval) {
          node.value.range = {
            min: rval.min,
            max: rval.max,
          }
        }
      }
    })
  }

  const onFieldBlur = async (node: FormNode) => {
    const target = getValidationTarget(node)
    if (node.value.listName) {
      findNode(root, node.value.listName)?.value.flushChange?.()
    }

    // Reveal only after queued changes have produced the latest validation messages.
    await queue.whenIdle()

    formTraverse(root, (current) => {
      if (getValidationTarget(current) === target) {
        current.value.validationRevealed = true
      }
    })
    setRootNode({ ...root })
  }

  const onExternalValidation = (owner: FormNode, target: FormNode, validation: ExternalValidation | null) => {
    const key = getValidationTarget(target)
    const entry = { ownerId: owner.id, validation }
    if (isSameExternalValidation(externalValidations.current.get(key), entry)) {
      return
    }

    externalValidations.current.set(key, entry)
    applyExternalValidations(root, externalValidations.current)
    setRootNode({ ...root })
  }

  const validateForSave = async (): Promise<ValidatedForm | null> => {
    const flush = (node?: FormNode): void => {
      if (!node) {
        return
      }

      flush(node.firstChild)
      node.value.flushChange?.()
      flush(node.nextSibling)
    }

    // Save must validate the latest input, including changes still waiting to debounce.
    flush(root)
    await queue.whenIdle()

    const newEntity = { ...currentEntityRef.current, fields: { ...currentEntityRef.current.fields } }
    const additionals = additionalEntitiesRef.current.map((item) => ({ ...item, fields: { ...item.fields } }))
    const clonedEntity = { ...newEntity, fields: { ...newEntity.fields } }
    const clonedAdditionals = additionals.map((item) => ({ ...item, fields: { ...item.fields } }))
    const entityContext = new EntityContext(newEntity, root, additionals, formModel)
    const clonedContext = new EntityContext(clonedEntity, root, clonedAdditionals, formModel)

    resetFormTree(entityContext)
    await runDataRulesAndValidations(entityContext, clonedContext)

    formTraverse(root, (node) => {
      node.value.validationRevealed = true
    })
    setRootNode({ ...root })

    if (!isTreeValid(root)) {
      return null
    }

    return {
      entity: currentEntityRef.current,
      additionalEntities: additionalEntitiesRef.current,
      rootNode: root,
    }
  }

  useEffect(() => {
    if (!validationRef) {
      return
    }

    validationRef.current = validateForSave
    return () => {
      validationRef.current = null
    }
  })

  /**
   * The function `isMsOnline` checks if a microservice with a specific name is online.
   * @param {string} msName - The `msName` parameter is a string that represents the name of a
   * microservice.
   * @returns The function `isMsOnline` returns a boolean value indicating whether the microservice
   * with the specified name is online and is in a healthy state. If the microservice with the given
   * name is found in the `microservices` array and its `isOk` property is true, the function returns
   * `true`. Otherwise, it returns `false`.
   */
  const isMsOnline = (msName: string) => {
    const msEntry = microservices.find((ms) => ms.name === msName)
    return msEntry && msEntry.isOk
  }

  const jumpToIssue = (issue: ValidationIssue) => {
    if (issue.tabPath.length) {
      const next: Record<string, number> = {}
      issue.tabPath.forEach((tab) => {
        const firstTab = findNode(rootNode, tab.groupId)
        let currentTab: FormNode | undefined = firstTab ?? undefined
        let visibleIndex = 0

        while (currentTab && (currentTab.value.type === 'Tab' || currentTab.value.type === 'CustomTab')) {
          const definition = currentTab.value.formDefinition
          const isCustomTab = definition?.type === 'CustomTab'
          const isAvailable =
            currentTab.value.visibility === 'Visible' &&
            (!isCustomTab || isMsOnline((definition as FormCustomTabDefinition).msProvider))

          if (isAvailable && currentTab.id === tab.tabId) {
            next[tab.groupId] = visibleIndex
            break
          }

          if (isAvailable) {
            visibleIndex += 1
          }
          currentTab = currentTab.nextSibling
        }
      })

      setSelectedTabs((current) => ({ ...current, ...next }))
      if (rootTabId && next[rootTabId] !== undefined) {
        onChangeTab?.(next[rootTabId])
      }
    }

    setPendingTarget((current) => ({
      id: issue.targetId,
      fallbackNodeId: issue.node.value.listName,
      sequence: (current?.sequence ?? 0) + 1,
    }))
  }

  const validationIssues = useMemo(() => getValidationIssues(rootNode), [rootNode])

  const getTabLabel = (node: FormNode, label: React.ReactNode): React.ReactNode => {
    if (tabsDirection !== 'vertical' || mode === 'Readonly') {
      return label
    }

    // Include nested sections using the panel's reveal and severity rules.
    const issues = validationIssues.filter((issue) => issue.tabPath.some((tab) => tab.tabId === node.id))
    return <ValidationTabLabel issues={issues}>{label}</ValidationTabLabel>
  }

  const buildLayout = (node: FormNode | undefined): JSX.Element[] => {
    if (!node) {
      return []
    }

    let result: JSX.Element[] = []
    let sibling: JSX.Element[] = []

    switch (node.value.type) {
      case 'Group': {
        const childs = buildLayout(node.firstChild)
        sibling = buildLayout(node.nextSibling)
        result.push(
          <Group key={node.id} node={node}>
            {childs}
          </Group>,
        )
        break
      }
      case 'Tab':
      case 'CustomTab': {
        const tabs: FormNode[] = getVisibleTabs(node)
        result.push(
          <TabsContainer key={node.id} $withPadding orientation={tabsDirection}>
            <Tabs
              hasCustomSubHeader={formModel.subheader?.customUI ? true : false}
              orientation={tabsDirection}
              startingTab={node.id === rootTabId ? startingTab : undefined}
              selectedTab={node.id === rootTabId && selectedTab !== undefined ? selectedTab : selectedTabs[node.id]}
              onChange={(index) => {
                setSelectedTabs((current) => ({ ...current, [node.id]: index }))
                if (node.id === rootTabId) {
                  onChangeTab?.(index)
                }
              }}
              sx={
                tabsDirection === 'vertical'
                  ? {
                      width: 'unset',
                      margin: '0',
                      height: 'fit-content',
                      'max-height': 'fit-content',
                      'min-width': '200px',
                    }
                  : {}
              }
              childs={tabs
                .map((e) => {
                  const def = e.value.formDefinition as FormTabDefinition
                  if (def.type === 'Tab') {
                    const childs = buildLayout(e.firstChild)
                    return {
                      label: getTabLabel(
                        e,
                        <TabLabel def={def} formName={formModel.name} entityName={entityModel.name} />,
                      ),
                      content: childs,
                    } as TabEntity
                  } else if (def.type === 'CustomTab') {
                    const customDef = e.value.formDefinition as FormCustomTabDefinition
                    //we want to show the custom tab only if the ms is online
                    if (isMsOnline(customDef.msProvider)) {
                      return {
                        label: getTabLabel(e, t(def.idml, def.description)),
                        content: <CustomTab tabDef={customDef} />,
                        isCustom: true,
                      } as TabEntity
                    }
                  }
                })
                .filter((e) => !!e)}
            />
          </TabsContainer>,
        )
        break
      }
      case 'View': {
        const def = node.value.formDefinition as FormViewDefinition
        sibling = buildLayout(node.nextSibling)
        if (entity && node.value.visibility === 'Visible') {
          result.push(
            <EmbeddedViewFederationWrapper
              entityName={def.entityName}
              moduleName={def.name}
              key={`${def.entityName}-${def.name}`}
            >
              <I18nWrapper entityName={def.entityName} moduleName={def.name}>
                <EmbeddedView
                  key={`${def.entityName}-${def.name}`}
                  entityName={def.entityName}
                  viewName={def.name}
                  containerEntity={entity}
                  viewDef={def}
                  modalRef={modalRef}
                  refreshVersion={embeddedViewsRefreshVersion + (targetedViewVersions[def.name] ?? 0)}
                  refreshView={refreshView}
                  onDataChanged={handleEmbeddedViewDataChanged}
                />
              </I18nWrapper>
            </EmbeddedViewFederationWrapper>,
          )
        }

        break
      }
      case 'Form': {
        const def = node.value.formDefinition as FormFormDefinition
        sibling = buildLayout(node.nextSibling)
        if (entity && node.value.visibility === 'Visible') {
          result.push(
            <EmbeddedFormFederationWrapper entityName={def.entityName} formName={def.name} key={node.id}>
              <I18nWrapper entityName={def.entityName} moduleName={def.name}>
                <EmbeddedForm
                  key={`${def.entityName}-${def.name}`}
                  entityName={def.entityName}
                  formName={def.name}
                  id={def.id}
                  parentId={def.parentId}
                  containerEntity={currentEntity}
                />
              </I18nWrapper>
            </EmbeddedFormFederationWrapper>,
          )
        }

        break
      }
      case 'EntityView': {
        const def = node.value.formDefinition as FormEntityViewDefinition
        sibling = buildLayout(node.nextSibling)
        result.push(
          <EmbeddedEntityViewFederationWrapper
            entityName={def.entityName}
            editForm={def.editForm}
            key={`${def.entityName}-${def.editForm}`}
          >
            <I18nWrapper entityName={def.entityName} moduleName={def.editForm}>
              <EntityView key={node.id} node={node} entity={currentEntity} />
            </I18nWrapper>
          </EmbeddedEntityViewFederationWrapper>,
        )
        break
      }
      default: {
        sibling = buildLayout(node.nextSibling)

        const behaviour: ConditionalFieldRendererBehaviourProps =
          mode === 'Readonly'
            ? { readonly: true }
            : { onChange: onFieldChange, onBlur: onFieldBlur, onExternalValidation }

        const reloadViews = (options?: ReloadViewsOptions) => {
          if (options) {
            options.views.forEach((viewName) => refreshView?.(viewName))
          }
        }

        if (mode === 'Readonly' && lib === 'nova') {
          result.push(
            <ConditionalReadonlyFieldRenderer
              {...behaviour}
              key={node.id}
              node={node}
              type={node.value.type as FieldType}
              divider={true}
            />,
          )
        } else {
          result.push(
            <ConditionalFieldRenderer
              {...behaviour}
              key={node.id}
              node={node}
              type={node.value.type as FieldType}
              onReload={reloadViews}
              moveToTab={moveToTab}
            />,
          )
        }

        break
      }
    }

    result = result.concat(...sibling)

    return result
  }

  /*
   * The panel only recovers errors the user cannot see: below the fold on a long form, or
   * behind another tab on a tabbed one. A short tabless form shows every error in place.
   * The form model's `LongForm` attribute overrides the measurement.
   *
   * Keyed on rootNode because a data rule pass replaces it once visibility has settled.
   */
  const showPanel = useMemo(() => getFormLength(rootNode, formModel) !== FormLength.Short, [rootNode, formModel])

  const classNames = ['FormContainer']
  if (className) {
    classNames.push(className)
  }
  if (isEmbedded) {
    classNames.push('FormContainer--embedded')
  }

  return (
    <FormContainer
      ref={formRef}
      className={classNames.join(' ')}
      $mode={mode}
      $centered={formModel?.isCentered()}
      $isEmbedded={isEmbedded}
    >
      {mode !== 'Readonly' && showPanel && <ValidationPanel issues={validationIssues} onJump={jumpToIssue} />}
      {buildLayout(rootNode).map((e) => e)}
    </FormContainer>
  )
}
